/**
 * 收款链接（可重复收款）。
 *
 * 与「收款单」（`invoices.tsx`）是两件事，界面上必须让人一眼分清：
 *
 * | | 收款单 | 收款链接（本页） |
 * |---|---|---|
 * | 收几笔 | 一笔，付完即关 | 无限笔，长期挂着 |
 * | 金额 | 开单时定死 | 定死**或**让付款人自己填（有上下限） |
 * | 通道 | 由路由决定 | 可以钉死一条，也可以让付款人挑 |
 *
 * ## 界面上必须说清楚的三件事
 *
 * 1. **上限是必填的，而且要说明白为什么。** 商户第一反应是「为什么不能不限」，
 *    这里不解释，他就会去猜一个大得没意义的数字。
 * 2. **反刷卡是冷却而不是封死。** 商户看到「暂停收款」会以为链接废了，
 *    要让他知道到点自动恢复，也可以手动点「启用」立刻恢复。
 * 3. **链接就是凭据。** 与收款单同一条规矩，泄露的后果是「有人替你付了钱」。
 */

import { useState } from 'react';
import { request } from '../lib/api.ts';
import { usePagedList, useMutation, useAsync } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import {
  Badge, Button, Card, CopyId, DetailGrid, ErrorBox, Field, ModeBadge, Modal,
  Money, Select, StatusBadge, TextInput,
} from '../components/ui.tsx';
import { DataTable, Pager, type Column } from '../components/table.tsx';
import { formatMinor, toMinor } from '../lib/money.ts';
import { formatDateTime } from '../lib/format.ts';
import type { Mode, PaymentLink, PaymentLinkCharge } from '../lib/types.ts';

const STATUSES = [
  { value: 'active', label: '收款中' },
  { value: 'disabled', label: '已停用' },
];

/** 服务端算好的实际状态 → 中文与色调。`status` 本身只有 active/disabled 两个值。 */
const EFFECTIVE_LABEL: Readonly<Record<string, string>> = {
  active: '收款中',
  disabled: '已停用',
  expired: '已过期',
  cooling_down: '暂停收款',
};

/** 一次收款的状态。`unresolved` 的措辞刻意不含「失败」二字 —— 它还没有结论。 */
const CHARGE_LABEL: Readonly<Record<string, string>> = {
  in_flight: '进行中',
  succeeded: '已收款',
  failed: '未成功',
  unresolved: '待确认',
};

/**
 * 平台侧失败码 → 给商户看的人话。
 *
 * 只收**商户自己能做点什么**的那几个。通道返回的拒付码不在这里 —— 那些由
 * `error_code` + `error_class` 原样呈现，因为它们的取值随通道而异，
 * 硬编一张表迟早与连接器漂移，而漂移之后显示的是一句**听起来很确定的错话**。
 */
const FAILURE_HINT: Readonly<Record<string, string>> = {
  NO_ELIGIBLE_CHANNEL: '没有可用通道 —— 检查通道是否停用、绑定是否撤销、限额是否打满',
  CARD_BRAND_NOT_ACCEPTED: '卡种不受支持 —— 付款人的卡不在该通道的卡种范围内，未发往通道，不产生费用',
  BILLING_INCOMPLETE: '账单信息不全 —— 该通道要求姓名、邮箱、电话、完整地址，付款人未填齐，未发往通道，不产生费用',
  CONFIG_ERROR: '通道配置有误 —— 多半是凭证填错或缺失',
  FORBIDDEN: '权限不足，该通道不允许这笔交易',
  INVALID_STATE: '链接或通道状态不允许收款',
  RATE_LIMITED: '触发限流',
  DECLINED: '通道拒付',
};

/** 错误分类 → 谁的问题。商户最关心的其实是这一层：该找持卡人还是找我们。 */
const CLASS_HINT: Readonly<Record<string, { label: string; tone: 'bad' | 'warn' | 'neutral' }>> = {
  hard_decline: { label: '卡本身不行', tone: 'bad' },
  soft_decline: { label: '临时拒绝，可重试或换通道', tone: 'warn' },
  network: { label: '结果未知', tone: 'warn' },
  config: { label: '我们这边配置问题', tone: 'bad' },
  unknown: { label: '未分类', tone: 'neutral' },
};

interface ChannelOption {
  readonly id: string;
  readonly label: string;
  readonly channel_code: string;
  readonly mode: string;
}

export function PaymentLinksPage() {
  const session = useSession();
  const [status, setStatus] = useState('');
  const [mode, setMode] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = usePagedList<PaymentLink>('/v1/payment_links', {
    status: status === '' ? null : status,
    mode: mode === '' ? null : mode,
  });

  const canCreate = session.can('admin') && session.me.org.status === 'active';

  const columns: readonly Column<PaymentLink>[] = [
    { key: 'title', header: '名目', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{row.title}</span>
        <span className="cell-muted mono" style={{ fontSize: 11 }}>{row.order_prefix}</span>
      </div>
    ) },
    ...(session.isMerchant ? [] : [{
      key: 'merchant',
      header: '商户',
      render: (row: PaymentLink) => <span>{row.merchant_name ?? row.merchant_org_id}</span>,
    }]),
    { key: 'amount', header: '金额', numeric: true, render: (row) => (
      row.amount_mode === 'fixed' && row.amount !== null
        ? <Money value={row.amount} currency={row.currency} />
        : (
          <span className="cell-muted mono" style={{ fontSize: 12 }}>
            {row.min_amount === null || row.max_amount === null ? '付款人自填' : (
              <>
                {formatMinor(row.min_amount, row.currency)}
                {' – '}
                {formatMinor(row.max_amount, row.currency)}
              </>
            )}
          </span>
        )
    ) },
    { key: 'channel', header: '通道', render: (row) => (
      <span className="cell-muted" style={{ fontSize: 12 }}>
        {row.channel_mode === 'auto' ? '自动路由'
          : row.channel_mode === 'fixed' ? '已指定'
          : '付款人可选'}
      </span>
    ) },
    { key: 'collected', header: '已收', numeric: true, render: (row) => (
      row.paid_count === '0'
        ? <span className="num cell-muted">—</span>
        : (
          <div className="stack tight" style={{ alignItems: 'flex-end' }}>
            <Money value={row.paid_total} currency={row.currency} />
            <span className="cell-muted mono" style={{ fontSize: 11 }}>{row.paid_count} 笔</span>
          </div>
        )
    ) },
    { key: 'mode', header: '模式', render: (row) => <ModeBadge mode={row.mode} /> },
    { key: 'status', header: '状态', render: (row) => (
      <StatusBadge status={EFFECTIVE_LABEL[row.status_effective] ?? row.status_effective} />
    ) },
    { key: 'created', header: '创建时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.created_at)}</span>
    ) },
  ];

  return (
    <>
      <div className="row">
        <h1>收款链接</h1>
        <span style={{ flex: 1 }} />
        {canCreate && <Button variant="primary" onClick={() => { setCreating(true); }}>建一条链接</Button>}
      </div>

      <div className="alert" data-tone="info">
        <span className="alert-title">一条链接长期挂着收款，付完一笔还能再付</span>
        <span>
          金额可以定死，也可以<strong>让付款人自己填</strong>（必须给上下限）；
          通道可以交给路由、钉死一条，或者<strong>让付款人在几条里挑</strong>。
          与「收款单」的区别是那个只收一笔，付完就关。
        </span>
      </div>

      <div className="filters">
        <Field label="状态">
          <Select value={status} onChange={setStatus} placeholder="全部" options={STATUSES} />
        </Field>
        <Field label="模式">
          <Select
            value={mode}
            onChange={setMode}
            placeholder="全部"
            options={[{ value: 'test', label: 'test' }, { value: 'live', label: 'live' }]}
          />
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.items}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          onRowClick={(row) => { setSelectedId(row.id); }}
          empty="还没有收款链接"
          emptyHint="适合长期挂着的收款场景：打赏、补差价、按次结算、线下报价后收款。"
        />
        <Pager
          page={list.page}
          hasNext={list.hasNext}
          hasPrev={list.hasPrev}
          onNext={list.next}
          onPrev={list.prev}
          loading={list.loading}
          count={list.items.length}
        />
      </Card>

      {creating && (
        <CreateLinkModal
          onClose={() => { setCreating(false); }}
          onDone={(link) => { setCreating(false); list.reload(); setSelectedId(link.id); }}
        />
      )}
      {selectedId !== null && (
        <LinkModal
          linkId={selectedId}
          onClose={() => { setSelectedId(null); }}
          onChanged={() => { list.reload(); }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// 建链接
// ---------------------------------------------------------------------------

function CreateLinkModal({
  onClose, onDone,
}: {
  onClose: () => void;
  onDone: (link: PaymentLink) => void;
}) {
  const session = useSession();

  const merchants = useAsync<{ data: { id: string; name: string; status: string }[] }>(
    session.isMerchant ? null : '/v1/orgs',
    { query: { type: 'merchant', limit: 200 } },
    [session.isMerchant],
  );
  const merchantOptions = (merchants.data?.data ?? [])
    .filter((org) => org.status === 'active')
    .map((org) => ({ value: org.id, label: org.name }));
  const [merchantOrgId, setMerchantOrgId] = useState('');
  const needsMerchant = !session.isMerchant;
  const merchantMissing = needsMerchant && merchantOrgId === '';
  const merchantsFailed = needsMerchant && !merchants.loading && merchants.error !== null;

  const [mode, setMode] = useState<Mode>('test');
  const [title, setTitle] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [description, setDescription] = useState('');
  const [expiresHours, setExpiresHours] = useState('');

  const [amountMode, setAmountMode] = useState<'fixed' | 'open'>('open');
  const [amount, setAmount] = useState('');
  const [minAmount, setMinAmount] = useState('1.00');
  const [maxAmount, setMaxAmount] = useState('');
  const [presetAmount, setPresetAmount] = useState('');

  const [channelMode, setChannelMode] = useState<'auto' | 'fixed' | 'payer_choice'>('auto');
  const [pinnedChannel, setPinnedChannel] = useState('');
  /** payer_choice 选中的通道 → 商户自定义的显示名（空串表示用通道自己的 label） */
  const [picked, setPicked] = useState<Record<string, string>>({});

  /*
   * 可选通道要按 (商户, mode) 现查。
   *
   * 不能拿「通道账户」整表来填这个下拉 —— 那张表是平台的全部通道，
   * 而这条链接只能用**绑给这个商户**的那几条。让商户在界面上挑到一条自己没有的通道，
   * 结果是服务端拒绝创建，他还得回来猜是哪一条不对。
   */
  const effectiveMerchant = merchantOrgId === '' ? session.me.org.id : merchantOrgId;
  const channels = useAsync<{ data: ChannelOption[] }>(
    '/v1/payment_links/channel_options',
    { query: { merchant_org_id: effectiveMerchant, mode } },
    [effectiveMerchant, mode],
  );
  const channelList = channels.data?.data ?? [];

  /** 本地先把金额转成最小单位，小数位超限这类错误当场暴露，不用发一次请求再被拒。 */
  function parse(value: string): bigint | null {
    if (value.trim() === '') return null;
    try {
      return toMinor(value, currency);
    } catch {
      return null;
    }
  }

  const minorAmount = parse(amount);
  const minorMin = parse(minAmount);
  const minorMax = parse(maxAmount);
  const minorPreset = parse(presetAmount);

  const amountProblem = ((): string | null => {
    if (amountMode === 'fixed') {
      if (amount.trim() === '') return null;
      if (minorAmount === null) return '金额格式不对';
      if (minorAmount <= 0n) return '金额必须大于 0';
      return null;
    }
    if (minAmount.trim() !== '' && minorMin === null) return '下限格式不对';
    if (maxAmount.trim() !== '' && minorMax === null) return '上限格式不对';
    if (minorMin !== null && minorMin <= 0n) return '下限必须大于 0';
    if (minorMax !== null && minorMin !== null && minorMax < minorMin) return '上限不能小于下限';
    if (presetAmount.trim() !== '') {
      if (minorPreset === null) return '预填金额格式不对';
      if (minorMin !== null && minorPreset < minorMin) return '预填金额低于下限';
      if (minorMax !== null && minorPreset > minorMax) return '预填金额高于上限';
    }
    return null;
  })();

  const pickedIds = Object.keys(picked);
  const incomplete =
    title.trim() === '' ||
    merchantMissing ||
    amountProblem !== null ||
    (amountMode === 'fixed' ? minorAmount === null : (minorMin === null || minorMax === null)) ||
    (channelMode === 'fixed' && pinnedChannel === '') ||
    (channelMode === 'payer_choice' && pickedIds.length === 0);

  const create = useMutation(async () =>
    await request<PaymentLink>('/v1/payment_links', {
      method: 'POST',
      body: {
        ...(merchantOrgId === '' ? {} : { merchant_org_id: merchantOrgId }),
        mode,
        title,
        currency: currency.toUpperCase(),
        ...(description === '' ? {} : { description }),
        amount_mode: amountMode,
        ...(amountMode === 'fixed'
          ? { amount: (minorAmount ?? 0n).toString() }
          : {
              min_amount: (minorMin ?? 0n).toString(),
              max_amount: (minorMax ?? 0n).toString(),
              ...(minorPreset === null ? {} : { preset_amount: minorPreset.toString() }),
            }),
        channel_mode: channelMode,
        ...(channelMode === 'fixed' ? { channel_account_id: pinnedChannel } : {}),
        ...(channelMode === 'payer_choice'
          ? {
              channels: pickedIds.map((id) => ({
                channel_account_id: id,
                ...(picked[id] === undefined || picked[id] === '' ? {} : { display_label: picked[id] }),
              })),
            }
          : {}),
        ...(expiresHours === '' ? {} : { expires_in_hours: Number(expiresHours) }),
      },
    }));

  return (
    <Modal
      title="建一条收款链接"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={create.pending || incomplete}
            onClick={() => { void create.run().then((result) => { if (result !== null) onDone(result); }); }}
          >
            {create.pending ? '创建中…' : '创建并取链接'}
          </Button>
        </>
      }
    >
      <ErrorBox error={create.error} />

      {needsMerchant && (
        merchants.loading ? (
          <Field label="收款商户"><span className="field-hint">正在读取商户列表…</span></Field>
        ) : merchantsFailed ? (
          <div className="alert" data-tone="bad">
            <span className="alert-title">商户列表没读出来</span>
            <span>
              这不代表你名下没有商户 —— 是这次请求失败了。请重试；一直不行的话多半是
              登录态过期了，退出重新登录即可。
            </span>
            <div style={{ marginTop: 10 }}>
              <Button onClick={() => { merchants.reload(); }}>重试</Button>
            </div>
          </div>
        ) : merchantOptions.length === 0 ? (
          <div className="alert" data-tone="warn">
            <span className="alert-title">你下面还没有可收款的商户</span>
            <span>
              收款链接的钱要落到某个<strong>商户</strong>账上 —— 通道是按商户绑定的。
              请先到「组织」建一个商户，并在「MID 下发」里给它绑一条通道。
            </span>
          </div>
        ) : (
          <Field label="收款商户" hint="这笔钱落到哪个商户账上">
            <Select
              value={merchantOrgId}
              onChange={setMerchantOrgId}
              placeholder="请选择"
              options={merchantOptions}
            />
          </Field>
        )
      )}

      <Field label="名目" hint="付款人会在页面最显眼的位置看到它">
        <TextInput value={title} onChange={setTitle} placeholder="例如：设计服务尾款" />
      </Field>

      <div className="grid cols-2">
        <Field label="模式" hint="test 链接只会走 test 通道，不产生真实扣款">
          <Select
            value={mode}
            onChange={(value) => {
              setMode(value as Mode);
              // 通道名单是按 mode 查的，换了模式旧的选择必然作废
              setPinnedChannel('');
              setPicked({});
            }}
            options={[{ value: 'test', label: 'test' }, { value: 'live', label: 'live' }]}
          />
        </Field>
        <Field label="币种">
          <TextInput value={currency} onChange={(value) => { setCurrency(value.toUpperCase()); }} />
        </Field>
      </div>

      {/* ── 金额 ── */}
      <div className="sep-label">金额</div>
      <Field label="金额方式">
        <Select
          value={amountMode}
          onChange={(value) => { setAmountMode(value as 'fixed' | 'open'); }}
          options={[
            { value: 'open', label: '付款人自己填（有上下限）' },
            { value: 'fixed', label: '定额，付款人改不了' },
          ]}
        />
      </Field>

      {amountMode === 'fixed' ? (
        <Field label="金额" hint={`按 ${currency} 的正常写法填，例如 129.00`}>
          <TextInput value={amount} numeric onChange={setAmount} />
        </Field>
      ) : (
        <>
          <div className="grid cols-3">
            <Field label="最低" hint="低于这个数付不了">
              <TextInput value={minAmount} numeric onChange={setMinAmount} />
            </Field>
            <Field label="最高" hint="必填">
              <TextInput value={maxAmount} numeric onChange={setMaxAmount} placeholder="例如 5000.00" />
            </Field>
            <Field label="预填" hint="选填，付款人可改">
              <TextInput value={presetAmount} numeric onChange={setPresetAmount} />
            </Field>
          </div>
          <div className="alert" data-tone="warn">
            <span className="alert-title">上限是必填的</span>
            <span>
              这是一条<strong>公开的</strong>收款链接，谁拿到都能付。没有上限，
              就等于给任何持有链接的人开了一个任意金额的入口 ——
              盗刷的卡会挑最大的数字来试。填一个你真的愿意一次性收到的数。
            </span>
          </div>
        </>
      )}

      {/* ── 通道 ── */}
      <div className="sep-label">通道</div>
      <Field
        label="走哪条通道"
        hint="不确定就选自动路由 —— 系统会按健康度、成本、限额挑一条。"
      >
        <Select
          value={channelMode}
          onChange={(value) => { setChannelMode(value as 'auto' | 'fixed' | 'payer_choice'); }}
          options={[
            { value: 'auto', label: '自动路由（推荐）' },
            { value: 'fixed', label: '钉死一条' },
            { value: 'payer_choice', label: '让付款人自己挑' },
          ]}
        />
      </Field>

      {channelMode !== 'auto' && (
        channels.loading ? (
          <span className="field-hint">正在读取可用通道…</span>
        ) : channelList.length === 0 ? (
          <div className="alert" data-tone="warn">
            <span className="alert-title">这个商户在 {mode} 模式下没有可用通道</span>
            <span>
              通道要先在「MID 下发」里绑给该商户、且双方都是启用状态才会出现在这里。
              没有可用通道时只能选「自动路由」。
            </span>
          </div>
        ) : channelMode === 'fixed' ? (
          <Field label="指定通道" hint="这条链接的每一笔都只走它；它不可用时收款会失败，不会自动换。">
            <Select
              value={pinnedChannel}
              onChange={setPinnedChannel}
              placeholder="请选择"
              options={channelList.map((ch) => ({ value: ch.id, label: `${ch.label}（${ch.channel_code}）` }))}
            />
          </Field>
        ) : (
          <Field
            label="付款人可选的通道"
            hint="勾选后可以改显示名 —— 付款人看到的是这个名字，不是通道账户的内部名称。"
          >
            <div className="stack">
              {channelList.map((ch) => {
                const on = picked[ch.id] !== undefined;
                return (
                  <div key={ch.id} className="row" style={{ alignItems: 'center', gap: 10 }}>
                    <label className="row" style={{ alignItems: 'center', gap: 7, minWidth: 220 }}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(event) => {
                          setPicked((prev) => {
                            const next = { ...prev };
                            if (event.target.checked) next[ch.id] = '';
                            else delete next[ch.id];
                            return next;
                          });
                        }}
                      />
                      <span>{ch.label}</span>
                      <span className="cell-muted mono" style={{ fontSize: 11 }}>{ch.channel_code}</span>
                    </label>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        value={picked[ch.id] ?? ''}
                        onChange={(value) => { setPicked((prev) => ({ ...prev, [ch.id]: value })); }}
                        placeholder={`付款人看到的名字（默认「${ch.label}」）`}
                        disabled={!on}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Field>
        )
      )}

      <Field label="说明" hint="选填。会显示在付款页上。">
        <textarea
          className="textarea"
          value={description}
          onChange={(event) => { setDescription(event.target.value); }}
          placeholder="例如：请按合同金额填写，备注留在邮箱字段里"
        />
      </Field>

      <Field label="有效期（小时）" hint="留空表示长期有效 —— 收款链接的常态就是长期挂着">
        <TextInput value={expiresHours} numeric onChange={setExpiresHours} placeholder="留空" />
      </Field>

      {amountProblem !== null && (
        <div className="alert" data-tone="warn"><span className="alert-title">{amountProblem}</span></div>
      )}

      {mode === 'live' && (
        <div className="alert" data-tone="bad">
          <span className="alert-title">这是 live 链接</span>
          <span>拿到链接的人会被真实扣款，而且可以反复付。确认配置无误再创建。</span>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 详情
// ---------------------------------------------------------------------------

function LinkModal({
  linkId, onClose, onChanged,
}: {
  linkId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const session = useSession();
  const [copied, setCopied] = useState(false);
  // 详情单独拉：列表里没有 channels / charges，而这两样才是商户真正要看的
  const detail = useAsync<PaymentLink>(`/v1/payment_links/${linkId}`, {}, [linkId]);
  const link = detail.data;

  const toggle = useMutation(async (next: 'disable' | 'enable') =>
    await request<PaymentLink>(`/v1/payment_links/${linkId}/${next}`, { method: 'POST' }));

  if (link === null || link === undefined) {
    return (
      <Modal title="收款链接" onClose={onClose} wide>
        <ErrorBox error={detail.error} />
        {detail.loading && <span className="field-hint">加载中…</span>}
      </Modal>
    );
  }

  const canToggle = session.can('admin');
  const live = link.status === 'active';

  return (
    <Modal
      title={link.title}
      onClose={onClose}
      wide
      footer={
        canToggle ? (
          <>
            <Button
              variant={live ? 'danger' : 'primary'}
              disabled={toggle.pending}
              onClick={() => {
                void toggle.run(live ? 'disable' : 'enable').then((result) => {
                  if (result !== null) { detail.reload(); onChanged(); }
                });
              }}
            >
              {toggle.pending ? '处理中…' : live ? '停用这条链接' : '重新启用'}
            </Button>
            <span style={{ flex: 1 }} />
            <Button onClick={onClose}>关闭</Button>
          </>
        ) : undefined
      }
    >
      <ErrorBox error={toggle.error} />

      <div className="grid cols-3">
        <div className="metric">
          <span className="metric-label">已收</span>
          <span className="metric-value">
            {formatMinor(link.paid_total, link.currency)}
            <span className="metric-currency">{link.currency}</span>
          </span>
        </div>
        <div className="metric">
          <span className="metric-label">笔数</span>
          <span className="metric-value">{link.paid_count}</span>
        </div>
        <div className="metric">
          <span className="metric-label">状态</span>
          <span style={{ marginTop: 4 }}>
            <StatusBadge status={EFFECTIVE_LABEL[link.status_effective] ?? link.status_effective} />
          </span>
        </div>
      </div>

      {link.status_effective === 'cooling_down' && (
        <div className="alert" data-tone="warn">
          <span className="alert-title">这条链接暂停收款中</span>
          <span>
            短时间内失败次数过多，系统自动暂停了它 —— 这是防止有人拿公开链接反复试卡号。
            <strong>到点会自动恢复</strong>（
            {link.locked_until === null ? '稍后' : formatDateTime(link.locked_until)}）；
            确认是正常付款人没付成的话，点两下「停用」「重新启用」可以立刻恢复。
          </span>
        </div>
      )}

      {link.status === 'active' && (
        <div className="stack tight">
          <span className="field-label">付款链接</span>
          <span className="field-hint">
            <strong>这条链接等于一张可以反复付款的凭据</strong> —— 拿到的人能付钱给你，
            而且不止一次。它改不了收款方、也读不到你的其它数据，
            {link.amount_mode === 'open' ? '金额只能填在你设的上下限之间。' : '金额也改不了。'}
          </span>
          <div className="secret-box">{link.pay_url}</div>
          <div className="row">
            <Button
              variant="primary"
              onClick={() => {
                void navigator.clipboard.writeText(link.pay_url).then(() => {
                  setCopied(true);
                  setTimeout(() => { setCopied(false); }, 1600);
                });
              }}
            >
              {copied ? '已复制到剪贴板' : '复制链接'}
            </Button>
            <a className="btn" href={link.pay_url} target="_blank" rel="noreferrer">
              打开付款页 ↗
            </a>
          </div>
        </div>
      )}

      <DetailGrid rows={[
        ['链接 ID', <CopyId key="id" id={link.id} full />],
        ['模式', <ModeBadge key="m" mode={link.mode} />],
        link.merchant_name === undefined ? null
          : ['收款商户', <span key="mc">{link.merchant_name}</span>],
        ['金额', <span key="a">
          {link.amount_mode === 'fixed' && link.amount !== null
            ? `定额 ${formatMinor(link.amount, link.currency)} ${link.currency}`
            : `付款人自填，${formatMinor(link.min_amount ?? '0', link.currency)} – ${formatMinor(link.max_amount ?? '0', link.currency)} ${link.currency}`}
        </span>],
        ['通道', <span key="ch">
          {link.channel_mode === 'auto' ? '自动路由'
            : link.channel_mode === 'fixed' ? '钉死一条'
            : '付款人自己挑'}
        </span>],
        ['单号前缀', <span key="o" className="mono">{link.order_prefix}</span>],
        ['有效期', <span key="x" className="mono">
          {link.expires_at === null ? '长期有效' : formatDateTime(link.expires_at)}
        </span>],
        ['创建时间', <span key="c" className="mono">{formatDateTime(link.created_at)}</span>],
        link.description === null ? null
          : ['说明', <span key="d" style={{ whiteSpace: 'pre-wrap' }}>{link.description}</span>],
      ]} />

      {link.channel_mode === 'payer_choice' && (link.channels?.length ?? 0) > 0 && (
        <div className="stack tight">
          <span className="field-label">付款人可选的通道</span>
          <span className="field-hint">
            付款人看到的是「显示名」，看不到通道账户本身。绑定被撤掉的选项会自动从付款页上消失。
          </span>
          <DetailGrid rows={(link.channels ?? []).map((ch) => [
            ch.display_label,
            <span key={ch.id} className="cell-muted">
              {ch.channel_label ?? ch.channel_account_id}
              {ch.binding_status !== 'active' && (
                <strong style={{ marginLeft: 8 }}>· 绑定已失效，付款页上不会出现</strong>
              )}
            </span>,
          ])} />
        </div>
      )}

      <ChargeList charges={link.charges ?? []} />

      {canToggle && live && (
        <div className="alert" data-tone="warn">
          <span className="alert-title">停用后链接立即失效</span>
          <span>已经发出去的链接会显示「已被商户关闭」。已经收到的钱不受影响，可以随时重新启用。</span>
        </div>
      )}
    </Modal>
  );
}

/**
 * 收款记录。
 *
 * `unresolved`（待确认）必须单独标出来并解释 —— 商户看到「未成功」和「待确认」
 * 会做完全不同的事：前者可以让客户重付，后者<strong>绝不能</strong>，
 * 因为那笔钱可能已经扣了，只是通道还没给出结论。
 */
function ChargeList({ charges }: { charges: readonly PaymentLinkCharge[] }) {
  if (charges.length === 0) {
    return (
      <div className="stack tight">
        <span className="field-label">收款记录</span>
        <span className="field-hint">还没有人通过这条链接付过款。</span>
      </div>
    );
  }

  const columns: readonly Column<PaymentLinkCharge>[] = [
    { key: 'order', header: '单号', render: (row) => (
      <span className="mono" style={{ fontSize: 12 }}>{row.order_no}</span>
    ) },
    { key: 'amount', header: '金额', numeric: true, render: (row) => (
      <Money value={row.amount} currency={row.currency} />
    ) },
    { key: 'state', header: '结果', render: (row) => (
      <StatusBadge status={CHARGE_LABEL[row.state] ?? row.state} />
    ) },
    { key: 'time', header: '时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.started_at)}</span>
    ) },
    { key: 'reason', header: '原因', render: (row) => <ChargeReason charge={row} /> },
    { key: 'payment', header: '交易', render: (row) => (
      row.payment_id === null
        ? <span className="cell-muted">—</span>
        : <CopyId id={row.payment_id} />
    ) },
  ];

  const unresolved = charges.filter((c) => c.state === 'unresolved' || c.state === 'in_flight');

  return (
    <div className="stack tight">
      <span className="field-label">收款记录</span>
      {unresolved.length > 0 && (
        <div className="alert" data-tone="warn">
          <span className="alert-title">有 {unresolved.length} 笔还没有定论</span>
          <span>
            通道还没告诉我们这几笔到底扣没扣款。系统正在自动查，查清后这里会自动更新。
            <strong>在此之前不要让付款人重付</strong> —— 钱可能已经扣了。
          </span>
        </div>
      )}
      <DataTable
        columns={columns}
        rows={charges}
        rowKey={(row) => row.id}
        empty="还没有收款"
      />
      <span className="field-hint">只显示最近 50 笔。完整流水请到「交易」页按单号前缀搜。</span>
    </div>
  );
}

export { EFFECTIVE_LABEL, CHARGE_LABEL };

/**
 * 一次收款失败的原因。
 *
 * 分三层显示，因为商户真正要判断的是「该找谁」：
 *
 *   1. **分类**（hard_decline / soft_decline / network / config）—— 这一层
 *      直接回答「是卡的问题还是我们的问题」，所以放在最前面。
 *   2. **通道原码 + 原话** —— 拿去跟通道客服对账用的，必须原样，绝不翻译。
 *   3. **平台失败码的人话** —— 只有平台侧那几个码有固定含义，才给提示。
 *
 * 拿不到 attempt 的（路由阶段就失败、没创建 payment）只显示第 3 层。
 * 成功与进行中的行显示 `—`，不是空白 —— 空白会让人以为是数据没加载出来。
 */
function ChargeReason({ charge }: { charge: PaymentLinkCharge }) {
  if (charge.state === 'succeeded' || charge.state === 'in_flight') {
    return <span className="cell-muted">—</span>;
  }

  const cls = charge.error_class === null ? undefined : CLASS_HINT[charge.error_class];
  const hint = charge.failure_code === null ? undefined : FAILURE_HINT[charge.failure_code];

  // 待确认：通道没给结论，这里绝不能显示成某种失败原因
  if (charge.state === 'unresolved') {
    return <span className="cell-muted">通道未给结论，系统正在查</span>;
  }

  return (
    <div className="stack tight" style={{ minWidth: 200 }}>
      <div className="row tight">
        {cls !== undefined && <Badge tone={cls.tone}>{cls.label}</Badge>}
        {charge.error_code !== null && (
          <span className="mono" style={{ fontSize: 12 }}>{charge.error_code}</span>
        )}
        {charge.channel_label !== null && (
          <span className="cell-muted" style={{ fontSize: 12 }}>· {charge.channel_label}</span>
        )}
      </div>
      {charge.error_message !== null && charge.error_message !== '' && (
        <span className="cell-muted" style={{ fontSize: 12 }}>{charge.error_message}</span>
      )}
      {/* 有通道原话时就不再重复平台码的泛泛提示，那只会把真正有用的信息挤下去 */}
      {charge.error_message === null && hint !== undefined && (
        <span className="cell-muted" style={{ fontSize: 12 }}>{hint}</span>
      )}
      {charge.error_message === null && hint === undefined && charge.failure_code !== null && (
        <span className="mono" style={{ fontSize: 12 }}>{charge.failure_code}</span>
      )}
    </div>
  );
}
